import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { bankingService } from '../../../services/bankingService';

const ImportHistoryModal = ({ isOpen, onClose }) => {
  const [imports, setImports] = useState([]);
  const [selectedImport, setSelectedImport] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadImportHistory();
    }
  }, [isOpen]);

  const loadImportHistory = async () => {
    try {
      setLoading(true);
      const importsData = await bankingService?.getStatementImports();
      setImports(importsData);
    } catch (err) {
      console.error('Error loading import history:', err);
      setError(err?.message || 'Failed to load import history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewErrors = async (importRecord) => {
    try {
      const errors = await bankingService?.getStatementImportErrors(importRecord?.id);
      setImportErrors(errors);
      setSelectedImport(importRecord);
      setShowErrorDetails(true);
    } catch (err) {
      console.error('Error loading import errors:', err);
      setError(err?.message || 'Failed to load import errors');
    }
  };

  const handleDownloadFile = async (importRecord) => {
    try {
      // Show loading state and clear any existing errors
      setError('');
      
      // Validate file path
      if (!importRecord?.filePath) {
        throw new Error('File path not available for this import');
      }
      
      // Add loading indicator (you could add a loading state for this specific download)
      const fileUrl = await bankingService?.downloadStatementFile(importRecord?.filePath);
      
      // Validate URL before attempting download
      if (!fileUrl) {
        throw new Error('Unable to generate download URL');
      }
      
      // Create download link
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = importRecord?.fileName || 'statement-file';
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
      console.log('File download initiated successfully');
    } catch (err) {
      console.error('Error downloading file:', err);
      
      // Provide user-friendly error messages with actionable guidance
      let errorMessage = 'Failed to download file';
      
      if (err?.message?.includes('File not found in storage') || 
          err?.message?.includes('Object not found') || 
          err?.message?.includes('not found')) {
        errorMessage = `File "${importRecord?.fileName}" is no longer available in storage. This can happen if:
        • The file was manually deleted from storage
        • The upload was incomplete or corrupted
        • Storage cleanup removed old files
        
        Solution: Try re-uploading the bank statement.`;
      } else if (err?.message?.includes('File path not available')) {
        errorMessage = 'This import record is missing file information. The file may not have been uploaded properly.';
      } else if (err?.message?.includes('access denied') || 
                 err?.message?.includes('permission')) {
        errorMessage = 'You do not have permission to download this file. Contact your administrator if this persists.';
      } else if (err?.message?.includes('Failed to generate download link')) {
        errorMessage = `${err?.message} This may be a temporary issue - please try again in a moment.`;
      } else if (err?.message?.includes('network') || 
                 err?.message?.includes('timeout') ||
                 err?.message?.includes('fetch')) {
        errorMessage = 'Network error occurred while downloading. Please check your connection and try again.';
      } else if (err?.message?.includes('Unable to generate download URL')) {
        errorMessage = 'Download service is temporarily unavailable. Please try again later.';
      } else {
        errorMessage = `Download error: ${err?.message || 'An unexpected error occurred'}. Please try again or contact support if the issue persists.`;
      }
      
      setError(errorMessage);
      
      // Auto-clear error after 20 seconds (increased for longer error messages)
      setTimeout(() => {
        setError('');
      }, 20000);
    }
  };

  const handleDeleteImport = async (importRecord) => {
    if (!confirm(`Are you sure you want to delete the import "${importRecord?.fileName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await bankingService?.deleteStatementImport(importRecord?.id);
      await loadImportHistory(); // Refresh list
    } catch (err) {
      console.error('Error deleting import:', err);
      setError(err?.message || 'Failed to delete import');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success';
      case 'failed':
        return 'text-error';
      case 'processing':
        return 'text-warning';
      case 'pending':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'processing':
        return '⏳';
      case 'pending':
        return '⏸️';
      default:
        return '❓';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Statement Import History</h2>
            <p className="text-sm text-muted-foreground mt-1">
              View and manage your bank statement imports
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-error/20 rounded-lg flex items-center justify-center">
                  <span className="text-error text-lg">⚠️</span>
                </div>
                <div>
                  <h4 className="font-medium text-error">Error</h4>
                  <p className="text-error/80 text-sm">{error}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError('')}
                iconName="X"
              />
            </div>
          </div>
        )}

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading import history...</p>
              </div>
            </div>
          ) : imports?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-muted-foreground text-2xl">📄</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No Statement Imports</h3>
              <p className="text-muted-foreground">
                You haven't imported any bank statements yet. Use the Import Statement feature to get started.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">
                  Recent Imports ({imports?.length})
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadImportHistory}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Refresh
                </Button>
              </div>

              {/* Import List */}
              <div className="space-y-3">
                {imports?.map(importRecord => (
                  <div key={importRecord?.id} className="bg-muted/10 border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-lg">{getStatusIcon(importRecord?.status)}</span>
                          <div>
                            <h4 className="font-medium text-foreground">{importRecord?.fileName}</h4>
                            <p className="text-sm text-muted-foreground">
                              {importRecord?.bankAccount?.accountName} ({importRecord?.bankAccount?.bankName})
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            importRecord?.status === 'completed' ? 'bg-success/10 text-success' :
                            importRecord?.status === 'failed' ? 'bg-error/10 text-error' :
                            importRecord?.status === 'processing'? 'bg-warning/10 text-warning' : 'bg-muted/20 text-muted-foreground'
                          }`}>
                            {importRecord?.status?.toUpperCase()}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">File Size:</span>
                            <p className="font-medium">{formatFileSize(importRecord?.fileSize)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Imported:</span>
                            <p className="font-medium text-success">
                              {importRecord?.importedTransactions || 0} transactions
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Failed:</span>
                            <p className="font-medium text-error">
                              {importRecord?.failedTransactions || 0} transactions
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Date:</span>
                            <p className="font-medium">{formatDate(importRecord?.createdAt)}</p>
                          </div>
                        </div>

                        {importRecord?.startDate && importRecord?.endDate && (
                          <div className="mt-2">
                            <span className="text-sm text-muted-foreground">
                              Transaction Period: {importRecord?.startDate} to {importRecord?.endDate}
                            </span>
                          </div>
                        )}

                        {importRecord?.errorMessage && (
                          <div className="mt-2 p-2 bg-error/10 rounded text-sm text-error">
                            {importRecord?.errorMessage}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {importRecord?.failedTransactions > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewErrors(importRecord)}
                            iconName="AlertCircle"
                            iconPosition="left"
                          >
                            View Errors
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadFile(importRecord)}
                          iconName="Download"
                          iconPosition="left"
                        >
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteImport(importRecord)}
                          iconName="Trash2"
                          className="text-error hover:text-error"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Details Modal */}
      {showErrorDetails && selectedImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Import Errors</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedImport?.fileName} - {importErrors?.length} errors found
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowErrorDetails(false)}
                iconName="X"
              />
            </div>

            <div className="p-6">
              {importErrors?.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-success text-lg">✅</span>
                  </div>
                  <h4 className="text-base font-medium text-foreground mb-2">No Errors Found</h4>
                  <p className="text-muted-foreground text-sm">
                    This import completed without any errors.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {importErrors?.map((error, index) => (
                    <div key={error?.id} className="bg-error/5 border border-error/20 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-medium text-foreground">Row {error?.rowNumber}</h5>
                        <span className="px-2 py-1 bg-error/10 text-error text-xs rounded">
                          {error?.errorType?.replace(/_/g, ' ')?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-error text-sm mb-3">{error?.errorMessage}</p>
                      {error?.rawData && (
                        <div className="bg-muted/20 rounded p-3">
                          <h6 className="text-xs font-medium text-muted-foreground mb-2">Raw Data:</h6>
                          <pre className="text-xs text-foreground overflow-x-auto">
                            {JSON.stringify(error?.rawData, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportHistoryModal;